export type TaskStatus =
  | 'pending'
  | 'dialing'
  | 'in_call'
  | 'completed'
  | 'failed';

export interface Task {
  id: string;
  phone_number: string;
  instruction: string;
  status: TaskStatus;
  twilio_call_sid: string | null;
  twilio_recording_sid: string | null;
  started_at: number | null;
  ended_at: number | null;
  summary: string | null;
  success: number | null;
  extracted_data: string | null;
  recording_path: string | null;
  error: string | null;
  created_at: number;
}

export interface TaskEvent {
  id: number;
  task_id: string;
  ts: number;
  kind: string;
  payload: string;
}

export interface LiveEvent {
  taskId: string;
  ts: number;
  kind: string;
  payload: unknown;
}

export async function createTask(input: {
  phoneNumber: string;
  instruction: string;
}): Promise<Task> {
  const res = await fetch('/api/tasks', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  const j = (await res.json()) as { task: Task };
  return j.task;
}

export async function listTasks(): Promise<Task[]> {
  const res = await fetch('/api/tasks');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const j = (await res.json()) as { tasks: Task[] };
  return j.tasks;
}

export async function hangupTask(id: string): Promise<void> {
  const res = await fetch(`/api/tasks/${id}/hangup`, { method: 'POST' });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
}

export async function getTask(id: string): Promise<{ task: Task; events: TaskEvent[] }> {
  const res = await fetch(`/api/tasks/${id}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as { task: Task; events: TaskEvent[] };
}

export function openLiveSocket(taskId: string, onEvent: (e: LiveEvent) => void): WebSocket {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const ws = new WebSocket(`${proto}//${window.location.host}/live/${taskId}`);
  ws.onmessage = (m) => {
    try {
      onEvent(JSON.parse(m.data) as LiveEvent);
    } catch {
      // ignore
    }
  };
  return ws;
}

export async function createLocalSession(prompt: string): Promise<Task> {
  const res = await fetch('/api/local', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ prompt }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return ((await res.json()) as { task: Task }).task;
}

export function openLocalSocket(taskId: string): WebSocket {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return new WebSocket(`${proto}//${window.location.host}/local/stream?taskId=${taskId}`);
}

// ───────────────── Auth ─────────────────

export interface AuthStatus {
  setup: boolean;            // has any user been created yet?
  authenticated: boolean;
  username: string | null;
}

export async function getAuthStatus(): Promise<AuthStatus> {
  const res = await fetch('/api/auth/status');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as AuthStatus;
}

export async function setupAdmin(input: {
  username: string;
  password: string;
}): Promise<void> {
  const res = await fetch('/api/auth/setup', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
}

export async function login(input: { username: string; password: string }): Promise<void> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST' });
}
